import { useTranslation } from "react-i18next";
import { BranchStatus } from "../../constants/branch-status";

interface StatusBadgeProps {
  status: BranchStatus;
  needsCloverLink?: boolean;
  className?: string;
}

const cloverDeactivatedConfig = {
  color: "bg-orange",
  ring: "ring-orange/30",
  icon: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
};

const statusConfig: Record<
  BranchStatus,
  { color: string; ring: string; icon: JSX.Element; title: string }
> = {
  [BranchStatus.APPROVED]: {
    color: "bg-green",
    ring: "ring-green/30",
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    title: "Aprobado",
  },
  [BranchStatus.REJECTED]: {
    color: "bg-red",
    ring: "ring-red/30",
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
    title: "Rechazado",
  },
  [BranchStatus.PENDING]: {
    color: "bg-yellow",
    ring: "ring-yellow/30",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        fill="white"
        stroke="white"
        strokeWidth="3"
        viewBox="0 0 1024 1024"
      >
        <path d="M511.9 183c-181.8 0-329.1 147.4-329.1 329.1s147.4 329.1 329.1 329.1c181.8 0 329.1-147.4 329.1-329.1S693.6 183 511.9 183zm0 585.2c-141.2 0-256-114.8-256-256s114.8-256 256-256 256 114.8 256 256-114.9 256-256 256z" />
        <path d="M548.6 365.7h-73.2v161.4l120.5 120.5 51.7-51.7-99-99z" />
      </svg>
    ),
    title: "Pendiente",
  },
  [BranchStatus.INCOMPLETE]: {
    color: "bg-gray-400",
    ring: "ring-gray-400/30",
    icon: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="5" />
      </svg>
    ),
    title: "Incompleto",
  },
};

export function StatusBadge({ status, needsCloverLink = false, className = "" }: StatusBadgeProps) {
  const { t } = useTranslation();
  const { color, ring, icon, title } =
    statusConfig[status] || statusConfig[BranchStatus.PENDING];

  const displayConfig = needsCloverLink
    ? {
        ...cloverDeactivatedConfig,
        title: t("branches.clover.needs-link-tooltip"),
      }
    : { color, ring, icon, title };

  return (
    <div
      title={displayConfig.title}
      className={`w-5 h-5 shrink-0 flex items-center justify-center rounded-full shadow-sm ring-2 ring-offset-2 ring-offset-[#2d2f32] ${displayConfig.color} ${displayConfig.ring} ${className}`}
    >
      {displayConfig.icon}
    </div>
  );
}
